import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Contenuto {
  competitor_handle: string; competitor_name: string; follower: number;
  permalink: string; cover_url: string | null; likes: number; comments: number;
  caption: string | null; transcript: string | null;
  performance?: string | null; spunto_alessandra?: string | null; posted_at?: string | null;
}
interface Competitor { handle: string; nome: string; follower: number; contenuti: Contenuto[]; }
interface Alert {
  id: string; alert_date: string; tipo: string; rilevanza: string;
  titolo: string; descrizione: string | null; spunto: string | null; fonte: string | null;
}

const Y = '#fdf751'; // solo riempimenti (badge), sopra ci va testo nero
const INK = '#13161C'; // scritte, frecce e icone: mai gialle
const fmtK = (n: number) => (n >= 1000 ? Math.round(n / 1000) + 'K' : String(n ?? 0));
const tipoLabel: Record<string, string> = { news: 'News di settore', picco: 'Contenuto virale di un competitor', post: 'Nuovo post di un competitor' };

// data leggibile: Oggi / Ieri / "ven 6 giu"
const fmtDay = (s: string | null) => {
  if (!s) return '';
  const d = new Date(s); const a = new Date(); const b = new Date(s);
  a.setHours(0, 0, 0, 0); b.setHours(0, 0, 0, 0);
  const diff = Math.round((a.getTime() - b.getTime()) / 86400000);
  if (diff === 0) return 'Oggi'; if (diff === 1) return 'Ieri'; if (diff > 1 && diff < 7) return `${diff} giorni fa`;
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
};

// priorità: dice chiaramente se vale la pena fare un contenuto
const priorita = (a: Alert): { label: string; hot: boolean; gray?: boolean } => {
  if (a.tipo === 'picco' || a.rilevanza === 'alta') return { label: 'Da fare il prima possibile · argomento caldo', hot: true };
  if (a.rilevanza === 'bassa') return { label: 'Solo per contesto', hot: false, gray: true };
  return { label: 'Da valutare', hot: false };
};

/**
 * Script del reel: in card se ne leggono le prime righe, il resto si apre con
 * un click. Le trascrizioni intere renderebbero la colonna un muro di testo.
 * L'invito compare solo se il testo è davvero tagliato.
 */
const TestoEspandibile: React.FC<{ testo: string; righe: number }> = ({ testo, righe }) => {
  const [aperto, setAperto] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  const [troncato, setTroncato] = useState(false);

  useLayoutEffect(() => {
    if (aperto) return;
    const misura = () => {
      const el = ref.current;
      if (el) setTroncato(el.scrollHeight > el.clientHeight + 1);
    };
    misura();
    window.addEventListener('resize', misura);
    return () => window.removeEventListener('resize', misura);
  }, [aperto, testo]);

  return (
    <>
      <p
        ref={ref}
        className="text-[14.5px] text-slate-800 leading-relaxed whitespace-pre-line"
        style={aperto ? undefined : { display: '-webkit-box', WebkitLineClamp: righe, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >
        {testo}
      </p>
      {(troncato || aperto) && (
        <button
          type="button"
          onClick={() => setAperto((v) => !v)}
          className="mt-1.5 text-[12px] font-semibold"
          style={{ color: INK }}
        >
          {aperto ? 'Mostra meno' : 'Leggi tutto lo script'}
        </button>
      )}
    </>
  );
};

/**
 * Riquadro mostrato quando una delle due colonne è ancora vuota. Oltre a dire
 * che non c'è nulla, invita il cliente a passarci le fonti: è da lui che
 * arrivano i profili e i siti da monitorare, e senza quelli la sezione resta
 * vuota senza che si capisca perché.
 */
const Vuoto: React.FC<{ testo: string }> = ({ testo }) => (
  <div
    className="p-5 sm:p-6 rounded-2xl"
    style={{ border: '1px dashed #DDE0E6', backgroundColor: '#F4F5F7' }}
  >
    <p className="text-sm text-slate-600 leading-relaxed">{testo}</p>
    <p className="text-[15px] leading-relaxed mt-3" style={{ color: '#13161C' }}>
      <span style={{ color: INK, fontWeight: 600 }}>Manda i link delle fonti al team ByMarke</span>{' '}
      per collegare chi vuoi monitorare: profili social dei concorrenti, testate e argomenti che ti interessano.
    </p>
  </div>
);

const CompetitorIntel: React.FC<{ clientId: string; clientName?: string }> = ({ clientId, clientName }) => {
  const [loading, setLoading] = useState(true);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/clients/${clientId}/competitors/public`);
        const j = await r.json();
        if (alive && j.success) { setCompetitors(j.data.competitors || []); setAlerts(j.data.alerts || []); }
      } catch { /* noop */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [clientId]);

  if (loading) return <div className="text-center py-16 text-slate-400">Carico i competitor…</div>;

  // ordina: prima i "caldi", poi per data
  const sorted = [...alerts].sort((x, y) => {
    const hx = priorita(x).hot ? 1 : 0, hy = priorita(y).hot ? 1 : 0;
    if (hx !== hy) return hy - hx;
    return new Date(y.alert_date).getTime() - new Date(x.alert_date).getTime();
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12 items-start">
      {/* Colonna 1: Segnalazioni di oggi */}
      <section>
        <div className="text-[11px] font-bold uppercase mb-1" style={{ color: INK, letterSpacing: '0.22em' }}>Segnalazioni</div>
        <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ letterSpacing: '-0.02em' }}>Su cosa conviene fare un contenuto</h2>
        <p className="text-sm text-slate-600 mb-5 max-w-2xl">Controllo automatico ogni mattina. Le segnalazioni <span style={{ color: INK, fontWeight: 600 }}>gialle</span> sono argomenti caldi su cui conviene pubblicare adesso; le altre sono da valutare o servono solo come contesto.</p>

        {sorted.length === 0 ? (
          <Vuoto testo="Nessuna segnalazione al momento: il controllo gira ogni mattina presto e popola questa sezione appena trova qualcosa di rilevante." />
        ) : (
          /* Una card per segnalazione: separa le notizie fra loro invece di
             lasciarle scorrere come un unico blocco di testo. */
          <div className="space-y-3 sm:space-y-4">
            {sorted.map((a) => {
              const p = priorita(a);
              return (
                <article
                  key={a.id}
                  className="p-5 sm:p-6 rounded-2xl"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #EBEDF1' }}
                >
                  {/* riga segnali: priorità + tipo + data */}
                  <div className="flex flex-wrap items-center gap-2.5 mb-2">
                    {p.hot ? (
                      <span className="text-[11px] font-bold uppercase px-2.5 py-1 rounded-full" style={{ background: Y, color: '#13161C', letterSpacing: '.04em' }}>🔥 {p.label}</span>
                    ) : (
                      <span className="text-[11px] font-bold uppercase px-2.5 py-1 rounded-full border" style={{ color: p.gray ? '#8B92A0' : '#5B6270', borderColor: p.gray ? '#EBEDF1' : '#DDE0E6', letterSpacing: '.04em' }}>{p.label}</span>
                    )}
                    <span className="text-[12px] text-slate-400">{tipoLabel[a.tipo] || a.tipo}</span>
                    <span className="text-[12px] text-slate-500">·</span>
                    <span className="text-[12px] font-semibold text-slate-600">{fmtDay(a.alert_date)}</span>
                  </div>

                  <div className="text-lg font-semibold text-slate-900 leading-snug">{a.titolo}</div>
                  {a.descrizione && <div className="text-[15px] text-slate-800 mt-1.5 leading-relaxed">{a.descrizione}</div>}
                  {a.spunto && (
                    <div className="flex gap-2 mt-3 text-[15px] leading-relaxed">
                      <span style={{ color: INK }}>→</span>
                      <div><span style={{ color: INK, fontWeight: 600 }}>Idea per il contenuto: </span><span className="text-slate-800">{a.spunto}</span></div>
                    </div>
                  )}
                  {a.fonte && <a href={a.fonte} target="_blank" rel="noreferrer" className="text-[12px] text-slate-500 hover:text-slate-800 mt-2.5 inline-block break-all">Leggi la fonte ↗</a>}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Colonna 2: Cosa pubblicano i competitor */}
      <section>
        <div className="text-[11px] font-bold uppercase mb-1" style={{ color: INK, letterSpacing: '0.22em' }}>Competitor</div>
        <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ letterSpacing: '-0.02em' }}>Cosa pubblicano i competitor</h2>
        <p className="text-sm text-slate-600 mb-8">Per ogni competitor i 4 reel pubblicati nell'ultimo mese con più like e commenti, con lo script già pronto da leggere e l'idea su cosa riproporre. Il badge <span style={{ color: INK, fontWeight: 600 }}>Virale</span> indica i contenuti che stanno andando forte, molto sopra la loro media.</p>

        {competitors.length === 0 && (
          <Vuoto testo="Non stiamo ancora seguendo nessun competitor per te: qui compariranno i loro 4 reel recenti più visti con lo script già pronto." />
        )}

        <div className="space-y-12">
          {competitors.map((c) => (
            <div key={c.handle}>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-lg font-semibold text-slate-900">{c.nome}</span>
                <span className="text-[13px] text-slate-500">{c.handle} · {fmtK(c.follower)} follower</span>
              </div>
              {/* Una card per contenuto: copertina e script restano dentro il
                  loro riquadro, così si distingue un reel dall'altro. */}
              <div className="space-y-3 sm:space-y-4">
                {c.contenuti.map((ct) => (
                  <article
                    key={ct.permalink}
                    className="flex gap-4 sm:gap-5 p-4 sm:p-5 rounded-2xl"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #EBEDF1' }}
                  >
                    {/* copertina, dimensione decente */}
                    <a href={ct.permalink} target="_blank" rel="noreferrer" className="shrink-0 block relative rounded-xl overflow-hidden" style={{ width: '128px', aspectRatio: '9/16', background: '#161616' }}>
                      {ct.cover_url && <img src={ct.cover_url} alt="" className="w-full h-full object-cover" />}
                      {ct.performance === 'virale' && (
                        <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: '#13161C', background: Y }}>🔥 Virale</span>
                      )}
                    </a>
                    {/* script + dati */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] mb-2.5">
                        <span className="font-semibold text-slate-900">♥ {ct.likes.toLocaleString('it-IT')}</span>
                        {ct.comments ? <span className="text-slate-400">{ct.comments.toLocaleString('it-IT')} commenti</span> : null}
                        {ct.posted_at && <span className="text-slate-500">{fmtDay(ct.posted_at)}</span>}
                      </div>
                      {ct.transcript ? (
                        <div>
                          <div className="text-[10px] font-bold uppercase mb-1" style={{ color: INK, letterSpacing: '.14em' }}>Script del reel</div>
                          <TestoEspandibile testo={ct.transcript} righe={5} />
                        </div>
                      ) : (
                        <span className="text-[13px] text-slate-500">Contenuto visivo, senza parlato.</span>
                      )}
                      {ct.spunto_alessandra && (
                        <div className="flex gap-2 mt-3 text-[14px] leading-relaxed">
                          <span style={{ color: INK }}>→</span>
                          <div><span style={{ color: INK, fontWeight: 600 }}>Da riproporre per {clientName || 'te'}: </span><span className="text-slate-800">{ct.spunto_alessandra}</span></div>
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CompetitorIntel;
